import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import moment from "moment";
import { formatStartDateIso, formatEndDateIso } from "../../../utils/dateUtils";


// Module-level UI helper functions matching KPI color theme
const renderPriorityBadge = (priority) => {
  const p = (priority || "Medium").toLowerCase();
  const isHigh = p === "high";
  const isLow = p === "low";
  const isMed = p === "medium" || p === "med";

  const text = isHigh ? "text-rose-600" : isMed ? "text-amber-600" : "text-slate-600";
  const dot = isHigh ? "bg-rose-600" : isMed ? "bg-amber-600" : "bg-slate-500";
  const label = isHigh ? "High" : isMed ? "Med" : isLow ? "Low" : "Med";

  return (
    <div className={`inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${text}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${dot}`}></div>
      <span>{label}</span>
    </div>
  );
};

const getStatusBadgeStyle = (statusLabel) => {
  const s = (statusLabel || "").toLowerCase().replace(/_/g, " ").replace(/-/g, " ");

  if (s.includes("progress") || s.includes("active") || s.includes("ongoing") || s.includes("new") || s.includes("created")) {
    return { bg: "bg-[#f9f0e1]", text: "text-[#ab6919]", border: "border-[#f3e3ca]/80", dot: "bg-[#ab6919]" };
  }
  if (s.includes("review") || s.includes("submitted")) {
    return { bg: "bg-[#e3eaf7]", text: "text-[#2956bd]", border: "border-[#d4e0f4]/80", dot: "bg-[#2956bd]" };
  }
  if (s.includes("completed") || s.includes("done") || s.includes("finished")) {
    return { bg: "bg-[#e3f5ed]", text: "text-[#1b8354]", border: "border-[#d2efdf]/80", dot: "bg-[#1b8354]" };
  }
  if (s.includes("delayed") || s.includes("overdue") || s.includes("late")) {
    return { bg: "bg-[#f9e5e5]", text: "text-[#c73838]", border: "border-[#f3d3d3]/80", dot: "bg-[#c73838]" };
  }
  if (s.includes("hold") || s.includes("waiting") || s.includes("dep") || s.includes("pending")) {
    return { bg: "bg-[#ece6fd]", text: "text-[#7055e8]", border: "border-[#dfd6fc]/80", dot: "bg-[#7055e8]" };
  }
  return { bg: "bg-[#eeece5]", text: "text-[#59554e]", border: "border-[#e2dfd7]/80", dot: "bg-[#59554e]" };
};

const getStatusColor = (statusName) => {
  const style = getStatusBadgeStyle(statusName);
  return `${style.bg} ${style.text} ${style.border}`;
};

const formatStatusLabel = (label) => {
  if (!label || typeof label !== "string") return "";
  const clean = label.replace(/_/g, " ").replace(/-/g, " ").trim();
  if (!clean) return "";
  return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
};

const getOwnerColor = (ownerStr) => {
  const s = (ownerStr || "").toUpperCase();
  if (s.includes("SR")) return "bg-emerald-700";
  if (s.includes("JD")) return "bg-[#2956bd]";
  if (s.includes("AF")) return "bg-[#ab6919]";
  if (s.includes("LS")) return "bg-[#7055e8]";
  const colors = ["bg-emerald-700", "bg-[#2956bd]", "bg-[#ab6919]", "bg-[#7055e8]", "bg-rose-700", "bg-slate-700"];
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash += s.charCodeAt(i);
  return colors[hash % colors.length];
};

const KPIGroups = ({ onSwitchView, currentViewMode = 'groups', worksCount = 0, projectsCount = 0 }) => {
  const dispatch = useDispatch();
  const { kpiProjects, taskTypes, eligibleUsers, loading } = useSelector((state) => state.kpi);
  const userInfoData = useSelector((state) => state.auth?.userInfoData);

  const [statusList, setStatusList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [groupError, setGroupError] = useState("");
  const [groupValidationErrors, setGroupValidationErrors] = useState({});
  const [workValidationErrors, setWorkValidationErrors] = useState({});
  const [isProjectFixed, setIsProjectFixed] = useState(false);

  // Accordion state
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  // Modals state
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);

  const [isCreateWorkOpen, setIsCreateWorkOpen] = useState(false);

  // Group options menu & edit/delete modals state
  const [openGroupMenuId, setOpenGroupMenuId] = useState(null);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const [editGroupData, setEditGroupData] = useState({ group_id: "", project_id: "", group_name: "" });
  const [editGroupErrors, setEditGroupErrors] = useState({});
  const [submittingEditGroup, setSubmittingEditGroup] = useState(false);

  const [isDeleteGroupOpen, setIsDeleteGroupOpen] = useState(false);
  const [deleteGroupData, setDeleteGroupData] = useState({ group_id: "", group_name: "", project_id: "" });
  const [submittingDeleteGroup, setSubmittingDeleteGroup] = useState(false);

  // Form states for modals
  const [groupFormData, setGroupFormData] = useState({
    project_id: "",
    group_name: "",
    group_description: ""
  });
  const [workFormData, setWorkFormData] = useState({
    task_name: "",
    task_description: "",
    task_priority: "Medium",
    task_due_date: "",
    assigned_kpi_points: "",
    task_kpi_type_id: "",
    project_id: "",
    group_id: "",
    assigned_to: []
  });

  const [submittingGroup, setSubmittingGroup] = useState(false);
  const [submittingWork, setSubmittingWork] = useState(false);

  const handleEditGroupSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = (editGroupData.group_name || "").trim();
    if (!trimmedName) {
      setEditGroupErrors({ group_name: "Please enter a group name." });
      return;
    }

    setSubmittingEditGroup(true);
    try {
      await dispatch.kpi.editProjectGroup({
        group_id: editGroupData.group_id,
        project_id: editGroupData.project_id,
        group_name: trimmedName,
      });
      setIsEditGroupOpen(false);
      dispatch.kpi.getKPIProjects();
    } catch (err) {
      console.error("Failed to edit group:", err);
      setEditGroupErrors({ group_name: err?.response?.data?.message || err?.message || "Failed to update group name." });
    } finally {
      setSubmittingEditGroup(false);
    }
  };

  const handleDeleteGroupConfirm = async () => {
    if (!deleteGroupData?.group_id) return;
    setSubmittingDeleteGroup(true);
    try {
      await dispatch.kpi.softDeleteProjectGroup(deleteGroupData.group_id);
      setIsDeleteGroupOpen(false);
      dispatch.kpi.getKPIProjects();
    } catch (err) {
      console.error("Failed to delete group:", err);
      alert(err?.response?.data?.message || err?.message || "Failed to delete group.");
    } finally {
      setSubmittingDeleteGroup(false);
    }
  };



  useEffect(() => {
    dispatch.kpi.getTaskTypes();
    dispatch.trashandStatus.getStatusData().then((res) => {
      if (res && res.data) {
        setStatusList(res.data);
      }
    });
  }, [dispatch]);

  // Fetch KPI Projects hierarchy with debounced search query (GET /api/kpi/projects?search=...)
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      dispatch.kpi.getKPIProjects(params);
    }, 300);

    return () => clearTimeout(timer);
  }, [dispatch, searchTerm]);

  useEffect(() => {
    if (isCreateWorkOpen) {
      if (workFormData.project_id) {
        dispatch.kpi.getEligibleUsers({ project_id: workFormData.project_id, limit: 100 });
      } else {
        dispatch.kpi.setEligibleUsers([]);
      }
    }
  }, [dispatch, isCreateWorkOpen, workFormData.project_id]);

  // Projects list derived EXCLUSIVELY from GET /api/kpi/projects API
  const kpiProjectsSelectList = React.useMemo(() => {
    const list = [];
    const seenIds = new Set();

    (kpiProjects || []).forEach((item) => {
      if (item.project) {
        const id = String(item.project._id || item.project.project_id || "");
        const name = item.project.project_name || item.project.name || "Project";
        if (id && !seenIds.has(id)) {
          seenIds.add(id);
          list.push({ _id: id, name });
        }
      }
    });

    return list;
  }, [kpiProjects]);

  // Set default expanded state when data arrives (only 1st project and 1st group open by default)
  useEffect(() => {
    if (kpiProjects && Array.isArray(kpiProjects)) {
      setExpandedProjects((prev) => {
        const pState = {};
        kpiProjects.forEach((item, idx) => {
          const pId = item.project?._id || `proj-${idx}`;
          pState[pId] = prev[pId] !== undefined ? prev[pId] : (idx === 0);
        });
        return { ...pState, ...prev };
      });
      setExpandedGroups((prev) => {
        const gState = {};
        kpiProjects.forEach((item, idx) => {
          const pId = item.project?._id || `proj-${idx}`;
          if (item.groups) {
            item.groups.forEach((g, gIdx) => {
              const gId = g.group?._id || `grp-${pId}-${gIdx}`;
              gState[gId] = prev[gId] !== undefined ? prev[gId] : (gIdx === 0);
            });
          }
        });
        return { ...gState, ...prev };
      });
    }
  }, [kpiProjects]);

  const projectsData = Array.isArray(kpiProjects) ? kpiProjects : [];

  // Options for inline status dropdown in task row
  const statusOptions =
    statusList && statusList.length > 0
      ? statusList.map((s) => ({ value: s._id, label: s.task_status }))
      : [
          { value: "todo", label: "Todo" },
          { value: "in_progress", label: "In progress" },
          { value: "waiting", label: "Waiting" },
          { value: "review", label: "Review" },
          { value: "completed", label: "Completed" }
        ];

  const toggleProject = (pId, isCurrentlyExpanded) => {
    setExpandedProjects((prev) => ({ ...prev, [pId]: !isCurrentlyExpanded }));
  };

  const toggleGroup = (gId, isCurrentlyExpanded) => {
    setExpandedGroups((prev) => ({ ...prev, [gId]: !isCurrentlyExpanded }));
  };

  // Inline status change handler
  const handleInlineStatusChange = async (task, selectedOption) => {
    const taskId = task._id || task.id;
    if (!taskId || !selectedOption || actionLoadingId) return;
    setActionLoadingId(taskId);
    try {
      await dispatch.kpi.updateWorkStatus({
        task_id: taskId,
        task_status: selectedOption.value,
        hideLoading: true
      });
      dispatch.kpi.getKPIProjects({
        hideLoading: true
      });
    } catch (err) {
      console.error("Failed to update status", err);
      alert(err?.response?.data?.message || "Failed to update status");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Drag and Drop State & Handlers
  const [dragOverTarget, setDragOverTarget] = useState(null);

  const handleDragStart = (e, task, parentProjectId, sourceGroupId) => {
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        taskId: task._id || task.id,
        taskName: task.task_name || task.name || task.title,
        projectId: parentProjectId,
        sourceGroupId: sourceGroupId || null
      })
    );
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e, targetKey) => {
    e.preventDefault();
    setDragOverTarget(targetKey);
  };

  const handleDragLeave = (e, targetKey) => {
    e.preventDefault();
    setDragOverTarget((prev) => (prev === targetKey ? null : prev));
  };

  const handleDropOnGroup = async (e, targetProjectId, targetGroupId) => {
    e.preventDefault();
    setDragOverTarget(null);
    try {
      const rawData = e.dataTransfer.getData("application/json");
      if (!rawData) return;
      const draggedData = JSON.parse(rawData);
      const { taskId, projectId, sourceGroupId } = draggedData;

      if (String(sourceGroupId) === String(targetGroupId)) {
        return; // Already in this group
      }

      const projId = targetProjectId || projectId;

      setActionLoadingId(taskId);
      await dispatch.kpi.editWork({
        task_id: taskId,
        project_id: projId,
        group_id: targetGroupId,
        hideLoading: true
      });
      dispatch.kpi.getKPIProjects({
        hideLoading: true
      });
    } catch (err) {
      console.error("Failed to move task to group", err);
      alert(err?.response?.data?.message || err?.message || "Failed to move task to group");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDropOnRootProject = async (e, targetProjectId) => {
    e.preventDefault();
    setDragOverTarget(null);
    try {
      const rawData = e.dataTransfer.getData("application/json");
      if (!rawData) return;
      const draggedData = JSON.parse(rawData);
      const { taskId, projectId, sourceGroupId } = draggedData;

      if (!sourceGroupId) {
        return; // Already direct project task
      }

      const projId = targetProjectId || projectId;

      setActionLoadingId(taskId);
      await dispatch.kpi.editWork({
        task_id: taskId,
        project_id: projId,
        group_id: null,
        hideLoading: true
      });
      dispatch.kpi.getKPIProjects({
        hideLoading: true
      });
    } catch (err) {
      console.error("Failed to remove task from group", err);
      alert(err?.response?.data?.message || err?.message || "Failed to remove task from group");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Modal actions
  const openCreateGroupModal = (projectId = "") => {
    const defaultProjId = projectId || (kpiProjectsSelectList[0]?._id || "");
    setGroupError("");
    setGroupValidationErrors({});
    setIsProjectFixed(!!projectId);
    setGroupFormData({
      project_id: defaultProjId,
      group_name: "",
      group_description: ""
    });
    setIsCreateGroupOpen(true);
  };

  const handleCreateGroupSubmit = async (e) => {
    e.preventDefault();
    setGroupError("");
    const errors = {};

    if (!groupFormData.project_id) {
      errors.project_id = "Please select a project.";
    }

    const trimmedName = (groupFormData.group_name || "").trim();
    if (!trimmedName) {
      errors.group_name = "Please enter a group name.";
    }

    if (Object.keys(errors).length > 0) {
      setGroupValidationErrors(errors);
      return;
    }

    // Client-side duplicate check within target project
    const targetProj = (baseProjectsData || []).find((p) => {
      const pId = p.project?._id || p.project_id || p._id || p.id;
      return String(pId) === String(groupFormData.project_id);
    });

    if (targetProj) {
      const existingGroups = targetProj.groups || targetProj.project_groups || [];
      const isDuplicate = existingGroups.some((g) => {
        const gName = (g.group_name || g.name || "").trim();
        return gName.toLowerCase() === trimmedName.toLowerCase();
      });

      if (isDuplicate) {
        setGroupValidationErrors({ group_name: `A group named "${trimmedName}" already exists in this project.` });
        return;
      }
    }

    setSubmittingGroup(true);
    try {
      await dispatch.kpi.createProjectGroup({
        project_id: groupFormData.project_id,
        group_name: trimmedName,
        group_description: groupFormData.group_description || undefined
      });
      setIsCreateGroupOpen(false);
      setGroupValidationErrors({});
      dispatch.kpi.getKPIProjects();
    } catch (err) {
      console.error("Failed to create group", err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to create group. A group with this name may already exist.";
      setGroupError(errMsg);
    } finally {
      setSubmittingGroup(false);
    }
  };

  const openCreateWorkModal = (projectId = "", groupId = "") => {
    const defaultProjId = projectId || (kpiProjectsSelectList[0]?._id || "");
    const todayStr = moment().format("YYYY-MM-DD");
    setWorkValidationErrors({});
    setWorkFormData({
      task_name: "",
      task_description: "",
      task_priority: "Medium",
      task_due_date: todayStr,
      assigned_kpi_points: taskTypes?.[0]?.score || "",
      task_kpi_type_id: taskTypes?.[0]?._id || "",
      project_id: defaultProjId,
      group_id: groupId || "",
      assigned_to: []
    });
    setIsCreateWorkOpen(true);
  };

  const handleCreateWorkSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    const todayStr = moment().format("YYYY-MM-DD");

    if (!workFormData.task_name || !workFormData.task_name.trim()) {
      errors.task_name = "Please enter work title.";
    }

    if (!workFormData.project_id) {
      errors.project_id = "Please select a project.";
    }

    if (!workFormData.task_due_date) {
      errors.task_due_date = "Please select due date.";
    } else if (workFormData.task_due_date < todayStr) {
      errors.task_due_date = "Due date cannot be in the past.";
    }

    if (!workFormData.assigned_to || workFormData.assigned_to.length === 0) {
      errors.assigned_to = "Please select an assignee.";
    }

    if (Object.keys(errors).length > 0) {
      setWorkValidationErrors(errors);
      return;
    }

    setSubmittingWork(true);
    try {
      const payload = {
        project_id: workFormData.project_id || null,
        group_id: workFormData.group_id || null,
        task_name: workFormData.task_name,
        task_description: workFormData.task_description,
        task_priority: workFormData.task_priority,
        task_start_date: formatStartDateIso(todayStr),
        task_end_date: formatEndDateIso(workFormData.task_due_date),
        assigned_kpi_points: workFormData.assigned_kpi_points ? Number(workFormData.assigned_kpi_points) : null,
        task_kpi_type_id: workFormData.task_kpi_type_id || (taskTypes.length > 0 ? taskTypes[0]._id : null),
        assigned_by: userInfoData?._id || null,
        assigned_to: workFormData.assigned_to
      };
      await dispatch.kpi.createWork(payload);
      setIsCreateWorkOpen(false);
      setWorkValidationErrors({});
      dispatch.kpi.getKPIProjects();
    } catch (err) {
      console.error("Failed to create work", err);
      alert(err?.response?.data?.message || "Failed to create work.");
    } finally {
      setSubmittingWork(false);
    }
  };

  // Calculate display projects: fallback to kpiProjectsSelectList if kpiProjects is empty
  const baseProjectsData = projectsData.length > 0
    ? projectsData
    : (kpiProjectsSelectList || []).map((p) => ({
        project: {
          _id: p._id,
          project_name: p.name || "Project",
          status: "ongoing"
        },
        tasks: [],
        groups: []
      }));

  // Filter project data by search term
  const filteredProjects = baseProjectsData.filter((item) => {
    if (!searchTerm.trim()) return true;
    const query = searchTerm.toLowerCase();
    const projName = (item.project?.name || item.project?.project_name || "").toLowerCase();
    if (projName.includes(query)) return true;
    const ungroupedMatch = (item.tasks || []).some((t) => (t.task_name || "").toLowerCase().includes(query));
    if (ungroupedMatch) return true;
    const groupMatch = (item.groups || []).some(
      (g) =>
        (g.group?.group_name || "").toLowerCase().includes(query) ||
        (g.tasks || []).some((t) => (t.task_name || "").toLowerCase().includes(query))
    );
    return groupMatch;
  });

  return (
    <div className="w-full">
      {/* Top Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Work Management</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            All KPI-tracked activities you own or follow
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-start sm:justify-end">
          <button
            type="button"
            onClick={() => openCreateGroupModal()}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-slate-800 transition-colors shadow-xs"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>Create Group</span>
          </button>
        </div>
      </div>

      {/* Top View Switcher Tabs (Works vs Projects) + Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between mb-6 border-b border-slate-200/80 gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onSwitchView && onSwitchView("works")}
            className={`flex items-center gap-2.5 px-5 py-3 text-sm font-bold transition-all border-b-2 ${
              currentViewMode === 'works'
                ? 'border-slate-900 text-slate-900 bg-white'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={currentViewMode === 'works' ? 'text-slate-900' : 'text-slate-400'}>
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
              <path d="m9 14 2 2 4-4"></path>
            </svg>
            <span>Works</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${currentViewMode === 'works' ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-500'}`}>
              {worksCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => onSwitchView && onSwitchView("groups")}
            className={`flex items-center gap-2.5 px-5 py-3 text-sm font-bold transition-all border-b-2 ${
              currentViewMode === 'groups'
                ? 'border-slate-900 text-slate-900 bg-white'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={currentViewMode === 'groups' ? 'text-slate-900' : 'text-slate-400'}>
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
            <span>Projects</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${currentViewMode === 'groups' ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-500'}`}>
              {projectsCount || baseProjectsData.length}
            </span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative w-full sm:w-64 pb-2.5 sm:pb-0">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search project, group or task..."
            className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all text-xs text-slate-900 bg-white shadow-2xs"
          />
        </div>
      </div>

      {/* Main Hierarchy Listing */}
      {loading && projectsData.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          <div className="animate-spin inline-block w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full mb-3"></div>
          <div>Loading project groups hierarchy...</div>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-slate-300 mb-3">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
          </svg>
          <div className="text-base font-semibold text-slate-700 mb-1">No Projects Found</div>
          <p className="text-xs text-slate-400 mb-4">Start by creating a project group or creating a work item.</p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => openCreateWorkModal()}
              className="px-4 py-2 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-slate-800 transition-colors"
            >
              + Create Work
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredProjects.map((item, pIdx) => {
            const project = item.project || {};
            const pId = project._id || `proj-${pIdx}`;
            const isPExpanded = expandedProjects[pId] !== undefined ? expandedProjects[pId] : pIdx === 0;
            const groups = item.groups || [];
            const ungroupedTasks = item.tasks || [];

            return (
              <div
                key={pId}
                className="bg-white rounded-2xl border border-slate-200/90 shadow-sm relative transition-all hover:shadow-md"
              >
                {/* Parent Card Header (Project Header matching Reference UI) */}
                <div className={`px-3.5 sm:px-5 py-2.5 sm:py-3 bg-[#f7f6f3] hover:bg-[#efeee7] transition-colors flex items-center justify-between gap-2.5 rounded-t-2xl ${!isPExpanded ? "rounded-b-2xl" : "border-b border-slate-200/80"}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      onClick={() => toggleProject(pId, isPExpanded)}
                      className="p-0.5 rounded text-slate-500 hover:text-slate-900 hover:bg-slate-200/60 transition-colors shrink-0"
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`transition-transform duration-200 ${isPExpanded ? "rotate-180" : ""}`}
                      >
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </button>

                    <h2 className="text-sm sm:text-base font-bold text-slate-900 tracking-tight truncate">
                      {project.name || project.project_name || "Unnamed Project"}
                    </h2>
                  </div>

                  {/* Right End Actions on Parent Box */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => openCreateWorkModal(pId)}
                      className="flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 transition-colors py-1 px-2.5 rounded-lg shadow-2xs whitespace-nowrap"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      <span>Add Work</span>
                    </button>

                    <button
                      onClick={() => openCreateGroupModal(pId)}
                      className="flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 transition-colors py-1 px-2.5 rounded-lg shadow-2xs whitespace-nowrap"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      <span>Add Group</span>
                    </button>
                  </div>
                </div>

                {/* Project Content Body */}
                {isPExpanded && (
                  <div className=" px-2 py-5 space-y-4">
                    {/* Groups Section */}
                    {groups.map((gItem, gIdx) => {
                      const groupObj = gItem.group || gItem || {};
                      const gId = groupObj._id || gItem._id || `grp-${pId}-${gIdx}`;
                      const gName = groupObj.group_name || groupObj.name || `Group ${gIdx + 1}`;
                      const isGExpanded = expandedGroups[gId] !== undefined ? expandedGroups[gId] : gIdx === 0;
                      const gTasks = gItem.tasks || groupObj.tasks || [];
                      const isDragOver = dragOverTarget === `grp-${gId}`;

                      const isGroupEditable = groupObj.is_editable !== undefined
                        ? Boolean(groupObj.is_editable)
                        : (gItem.is_editable !== undefined ? Boolean(gItem.is_editable) : true);

                      const isGroupDeleteable = groupObj.is_deleteable !== undefined
                        ? Boolean(groupObj.is_deleteable)
                        : (groupObj.is_deletable !== undefined
                          ? Boolean(groupObj.is_deletable)
                          : (gItem.is_deleteable !== undefined
                            ? Boolean(gItem.is_deleteable)
                            : (gItem.is_deletable !== undefined ? Boolean(gItem.is_deletable) : true)));

                      return (
                        <div
                          key={gId}
                          onDragOver={handleDragOver}
                          onDragEnter={(e) => handleDragEnter(e, `grp-${gId}`)}
                          onDragLeave={(e) => handleDragLeave(e, `grp-${gId}`)}
                          onDrop={(e) => handleDropOnGroup(e, pId, gId)}
                          className={`border rounded-xl transition-all duration-200 relative ${
                            isDragOver
                              ? "border-emerald-500 bg-emerald-50/70 ring-2 ring-emerald-500/30 shadow-md"
                              : "border-slate-200 bg-slate-50/40"
                          }`}
                        >
                          {/* Group Header */}
                          <div className={`px-3.5 sm:px-4 py-2.5 sm:py-3 bg-[#f7f6f3] hover:bg-[#efeee7] transition-colors flex items-center justify-between gap-2 rounded-t-xl ${!isGExpanded ? "rounded-b-xl" : "border-b border-slate-200/70"}`}>
                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                              <button
                                onClick={() => toggleGroup(gId, isGExpanded)}
                                className="w-7 h-7 rounded-lg bg-blue-50/90 border border-blue-200/90 text-blue-600 hover:bg-blue-100 transition-colors flex items-center justify-center shrink-0 shadow-2xs"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className={`transition-transform duration-200 ${isGExpanded ? "rotate-180" : ""}`}
                                >
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </button>
                              
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400 shrink-0">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                              </svg>

                              <span className="text-sm font-bold text-slate-900 whitespace-nowrap">
                                {gName}
                              </span>

                              <span className="hidden sm:inline-block text-xs font-semibold px-2 py-0.5 rounded-md bg-slate-200/70 text-slate-600 shrink-0 font-mono">
                                {gTasks.length} {gTasks.length === 1 ? "task" : "tasks"}
                              </span>
                            </div>

                            <div className="flex items-center gap-1.5 shrink-0">
                              <button
                                onClick={() => openCreateWorkModal(pId, gId)}
                                className="text-[11px] font-bold bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 transition-colors flex items-center gap-1 px-2.5 py-1 rounded-lg shadow-2xs whitespace-nowrap"
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <line x1="12" y1="5" x2="12" y2="19"></line>
                                  <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                <span>Add Work</span>
                              </button>

                              {/* 3 Vertical Dots Menu Button */}
                              {(isGroupEditable || isGroupDeleteable) && (
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenGroupMenuId(openGroupMenuId === gId ? null : gId);
                                    }}
                                    className="w-7 h-7 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 flex items-center justify-center transition-colors shadow-2xs"
                                    title="Group Actions"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="5" r="1" fill="currentColor" />
                                      <circle cx="12" cy="12" r="1" fill="currentColor" />
                                      <circle cx="12" cy="19" r="1" fill="currentColor" />
                                    </svg>
                                  </button>

                                  {/* Dropdown Menu */}
                                  {openGroupMenuId === gId && (
                                    <>
                                      <div
                                        className="fixed inset-0 z-20"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenGroupMenuId(null);
                                        }}
                                      />
                                      <div className="absolute right-0 mt-1 w-36 bg-white rounded-xl shadow-lg border border-slate-200 z-30 py-1 overflow-hidden animate-fadeIn text-xs">
                                        {isGroupEditable && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditGroupData({ group_id: gId, project_id: pId, group_name: gName });
                                              setEditGroupErrors({});
                                              setIsEditGroupOpen(true);
                                              setOpenGroupMenuId(null);
                                            }}
                                            className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-semibold flex items-center gap-2"
                                          >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                            <span>Edit Group</span>
                                          </button>
                                        )}
                                        {isGroupDeleteable && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setDeleteGroupData({ group_id: gId, group_name: gName, project_id: pId });
                                              setIsDeleteGroupOpen(true);
                                              setOpenGroupMenuId(null);
                                            }}
                                            className={`w-full text-left px-3 py-2 text-rose-600 hover:bg-rose-50 font-semibold flex items-center gap-2 ${isGroupEditable ? 'border-t border-slate-100' : ''}`}
                                          >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <polyline points="3 6 5 6 21 6" />
                                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                            </svg>
                                            <span>Delete Group</span>
                                          </button>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Visual Drop Banner */}
                          {isDragOver && (
                            <div className="bg-emerald-600 text-white text-xs font-bold px-4 py-2 text-center animate-pulse flex items-center justify-center gap-1.5 shadow-inner">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="3" x2="12" y2="15"></line>
                              </svg>
                              Release to move task to group "{gName}"
                            </div>
                          )}

                          {/* Group Tasks List */}
                          {isGExpanded && (
                            <div className="p-2 sm:p-2.5 space-y-1.5 min-h-[40px]">
                              {gTasks.length === 0 ? (
                                <div className="py-3 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg bg-white">
                                  No tasks in this group yet. Drag tasks here to move to {gName}.
                                </div>
                              ) : (
                                gTasks.map((task) => (
                                  <TaskRowCard
                                    key={task._id || task.id}
                                    task={task}
                                    parentProjectId={pId}
                                    currentGroupId={gId}
                                    handleDragStart={handleDragStart}
                                    statusOptions={statusOptions}
                                    handleInlineStatusChange={handleInlineStatusChange}
                                    renderPriorityBadge={renderPriorityBadge}
                                    getStatusColor={getStatusColor}
                                    actionLoadingId={actionLoadingId}
                                  />
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Direct Project Tasks Drop Zone (Ungrouped Root Tasks) */}
                    {(() => {
                      const isRootDragOver = dragOverTarget === `root-${pId}`;
                      return (
                        <div
                          onDragOver={handleDragOver}
                          onDragEnter={(e) => handleDragEnter(e, `root-${pId}`)}
                          onDragLeave={(e) => handleDragLeave(e, `root-${pId}`)}
                          onDrop={(e) => handleDropOnRootProject(e, pId)}
                          className={`mt-3 pt-3 border-t border-slate-200/80 rounded-xl p-2 sm:p-2.5 transition-all duration-200 ${
                            isRootDragOver
                              ? "border-amber-500 bg-amber-50/70 ring-2 ring-amber-500/30 shadow-md"
                              : "border-transparent bg-slate-50/20"
                          }`}
                        >
                          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 px-1 flex items-center justify-between">
                            <span>Ungrouped</span>
                            <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full text-slate-600 font-bold">
                              {ungroupedTasks.length}
                              </span>
                            </div>

                          {isRootDragOver && (
                            <div className="bg-amber-600 text-white text-xs font-bold px-4 py-2 mb-3 rounded-lg text-center animate-pulse flex items-center justify-center gap-1.5 shadow-inner">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="3" x2="12" y2="15"></line>
                              </svg>
                              Release to remove task from group and move to Project root
                            </div>
                          )}

                          <div className="space-y-2 min-h-[48px]">
                              {ungroupedTasks.length === 0 ? (
                              <div className="py-3 border border-dashed border-slate-200 rounded-lg text-center text-xs text-slate-400 bg-white/60">
                                Drag tasks here to remove from group and place directly in project
                                </div>
                              ) : (
                                ungroupedTasks.map((task) => (
                                  <TaskRowCard
                                    key={task._id || task.id}
                                    task={task}
                                    parentProjectId={pId}
                                    currentGroupId={null}
                                    handleDragStart={handleDragStart}
                                    statusOptions={statusOptions}
                                    handleInlineStatusChange={handleInlineStatusChange}
                                    renderPriorityBadge={renderPriorityBadge}
                                    getStatusColor={getStatusColor}
                                    actionLoadingId={actionLoadingId}
                                  />
                                ))
                              )}
                            </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}





      {/* Modal: Create Group */}
      {isCreateGroupOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-base font-bold text-slate-900">Create Project Group</h3>
              <button
                onClick={() => setIsCreateGroupOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-200/60"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateGroupSubmit} className="p-6 space-y-4">
              {groupError && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl flex items-center justify-between gap-2 shadow-2xs animate-in fade-in duration-150">
                  <div className="flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="shrink-0 text-rose-500">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{groupError}</span>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => setGroupError("")}
                    className="text-rose-400 hover:text-rose-600 font-bold p-0.5"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Project Field: Non-editable when adding under a project */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Project {isProjectFixed ? "" : <span className="text-rose-500">*</span>}
                </label>
                {isProjectFixed ? (
                  <div className="w-full px-3.5 py-2.5 bg-slate-100/90 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 select-none flex items-center gap-2">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-500">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <span>
                      {kpiProjectsSelectList.find((p) => String(p._id) === String(groupFormData.project_id))?.name || "Selected Project"}
                    </span>
                  </div>
                ) : (
                  <>
                    <Select
                      options={kpiProjectsSelectList.map((p) => ({ value: p._id, label: p.name }))}
                      className="text-sm text-slate-900"
                      classNamePrefix="select"
                      placeholder="Choose project..."
                      onChange={(opt) => {
                        setGroupError("");
                        setGroupValidationErrors((prev) => ({ ...prev, project_id: "" }));
                        setGroupFormData({ ...groupFormData, project_id: opt ? opt.value : '' });
                      }}
                      value={kpiProjectsSelectList.filter((p) => p._id === groupFormData.project_id).map((p) => ({ value: p._id, label: p.name }))[0] || null}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: groupValidationErrors.project_id ? '#f43f5e' : '#e2e8f0',
                          boxShadow: groupValidationErrors.project_id ? '0 0 0 2px rgba(244, 63, 94, 0.2)' : 'none',
                          borderRadius: '0.75rem',
                          padding: '2px',
                          '&:hover': {
                            borderColor: groupValidationErrors.project_id ? '#f43f5e' : '#0f172a'
                          }
                        })
                      }}
                    />
                    {groupValidationErrors.project_id && (
                      <p className="text-[11px] font-semibold text-rose-500 mt-1">{groupValidationErrors.project_id}</p>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Group Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Sprint 1 Feature Tasks..."
                  value={groupFormData.group_name}
                  onChange={(e) => {
                    setGroupError("");
                    setGroupValidationErrors((prev) => ({ ...prev, group_name: "" }));
                    setGroupFormData({ ...groupFormData, group_name: e.target.value });
                  }}
                  className={`w-full px-3.5 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all ${
                    groupValidationErrors.group_name || groupError ? 'border-rose-500 ring-2 ring-rose-500/20' : 'border-slate-200 focus:ring-slate-900/10 focus:border-slate-900'
                  }`}
                />
                {groupValidationErrors.group_name && (
                  <p className="text-[11px] font-semibold text-rose-500 mt-1">{groupValidationErrors.group_name}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Group Description</label>
                <textarea
                  rows="3"
                  placeholder="Describe group scope..."
                  value={groupFormData.group_description}
                  onChange={(e) => setGroupFormData({ ...groupFormData, group_description: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 focus:outline-none resize-none"
                ></textarea>
              </div>

              <div className="pt-3 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsCreateGroupOpen(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingGroup}
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 shadow-xs"
                >
                  {submittingGroup ? "Creating..." : "Create Group"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}





      {/* Modal: Create Work */}
      {isCreateWorkOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-[9999] flex items-center justify-center p-4 sm:p-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center shrink-0">
              <h3 className="text-base font-bold text-slate-900">Create Work Item</h3>
              <button
                onClick={() => setIsCreateWorkOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-200/60"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateWorkSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">
                    Work Title <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Task title..."
                    value={workFormData.task_name}
                    onChange={(e) => {
                      setWorkValidationErrors((prev) => ({ ...prev, task_name: "" }));
                      setWorkFormData({ ...workFormData, task_name: e.target.value });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none transition-all ${
                      workValidationErrors.task_name ? 'border-rose-500 ring-2 ring-rose-500/20' : 'border-slate-200 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900'
                    }`}
                  />
                  {workValidationErrors.task_name && (
                    <p className="text-[11px] font-semibold text-rose-500 mt-1">{workValidationErrors.task_name}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">Work Type</label>
                    <Select
                      options={(taskTypes || []).map((t) => ({ value: t._id, label: `${t.name} (${t.score} pts)` }))}
                      className="text-sm text-slate-900"
                      classNamePrefix="select"
                      placeholder="Select type..."
                      onChange={(opt) => {
                        const typeId = opt ? opt.value : "";
                        const selected = (taskTypes || []).find((t) => t._id === typeId);
                        setWorkFormData({
                          ...workFormData,
                          task_kpi_type_id: typeId,
                          assigned_kpi_points: selected?.score !== undefined ? selected.score : workFormData.assigned_kpi_points
                        });
                      }}
                      value={(taskTypes || []).filter((t) => t._id === workFormData.task_kpi_type_id).map((t) => ({ value: t._id, label: `${t.name} (${t.score} pts)` }))[0] || null}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#e2e8f0',
                          borderRadius: '0.5rem',
                          padding: '1px',
                          boxShadow: 'none',
                          '&:hover': { borderColor: '#0f172a' }
                        })
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">
                      Project <span className="text-rose-500">*</span>
                    </label>
                    <Select
                      options={kpiProjectsSelectList.map((p) => ({ value: p._id, label: p.name }))}
                      className="text-sm text-slate-900"
                      classNamePrefix="select"
                      placeholder="Select project..."
                      onChange={(opt) => {
                        setWorkValidationErrors((prev) => ({ ...prev, project_id: "", assigned_to: "" }));
                        setWorkFormData({ ...workFormData, project_id: opt ? opt.value : '', group_id: "", assigned_to: [] });
                      }}
                      value={kpiProjectsSelectList.map((p) => ({ value: p._id, label: p.name })).find((p) => p.value === workFormData.project_id) || null}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: workValidationErrors.project_id ? '#f43f5e' : '#e2e8f0',
                          boxShadow: workValidationErrors.project_id ? '0 0 0 2px rgba(244, 63, 94, 0.2)' : 'none',
                          borderRadius: '0.5rem',
                          padding: '1px',
                          '&:hover': { borderColor: workValidationErrors.project_id ? '#f43f5e' : '#0f172a' }
                        })
                      }}
                    />
                    {workValidationErrors.project_id && (
                      <p className="text-[11px] font-semibold text-rose-500 mt-1">{workValidationErrors.project_id}</p>
                    )}
                  </div>
                </div>

                {/* Group dropdown populated based on selected project */}
                {workFormData.project_id && (
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">Group (Optional)</label>
                    <Select
                      options={[{ value: "", label: "No Group (Add directly to Project)" }, ...(projectsData.find((p) => p.project?._id === workFormData.project_id)?.groups || []).map((g, gIdx) => ({ value: g.group?._id, label: g.group?.group_name || `Group ${gIdx + 1}` }))]}
                      className="text-sm text-slate-900"
                      classNamePrefix="select"
                      placeholder="No Group (Add directly to Project)"
                      onChange={(opt) => setWorkFormData({ ...workFormData, group_id: opt ? opt.value : '' })}
                      value={[{ value: "", label: "No Group (Add directly to Project)" }, ...(projectsData.find((p) => p.project?._id === workFormData.project_id)?.groups || []).map((g, gIdx) => ({ value: g.group?._id, label: g.group?.group_name || `Group ${gIdx + 1}` }))].find((g) => g.value === workFormData.group_id) || null}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#e2e8f0',
                          borderRadius: '0.5rem',
                          padding: '1px',
                          boxShadow: 'none',
                          '&:hover': { borderColor: '#0f172a' }
                        })
                      }}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">Priority</label>
                    <Select
                      options={[
                        { value: "Low", label: "Low" },
                        { value: "Medium", label: "Medium" },
                        { value: "High", label: "High" }
                      ]}
                      className="text-sm text-slate-900"
                      classNamePrefix="select"
                      placeholder="Select priority..."
                      onChange={(opt) => setWorkFormData({ ...workFormData, task_priority: opt ? opt.value : 'Medium' })}
                      value={{ value: workFormData.task_priority, label: workFormData.task_priority }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#e2e8f0',
                          borderRadius: '0.5rem',
                          padding: '1px',
                          boxShadow: 'none',
                          '&:hover': { borderColor: '#0f172a' }
                        })
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">
                      Due Date <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={workFormData.task_due_date}
                      onChange={(e) => {
                        setWorkValidationErrors((prev) => ({ ...prev, task_due_date: "" }));
                        setWorkFormData({ ...workFormData, task_due_date: e.target.value });
                      }}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none transition-all ${
                        workValidationErrors.task_due_date ? 'border-rose-500 ring-2 ring-rose-500/20' : 'border-slate-200 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900'
                      }`}
                    />
                    {workValidationErrors.task_due_date && (
                      <p className="text-[11px] font-semibold text-rose-500 mt-1">{workValidationErrors.task_due_date}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">KPI Points</label>
                    <input
                      type="number"
                      readOnly
                      disabled
                      value={workFormData.assigned_kpi_points}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-semibold text-center bg-slate-100 text-slate-700 cursor-not-allowed select-none focus:outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">
                    Assignee <span className="text-rose-500">*</span>
                  </label>
                  <Select
                    options={(eligibleUsers || []).map((u) => ({ value: u._id || u.id, label: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || u.email }))}
                    className="text-sm text-slate-900"
                    placeholder={workFormData.project_id ? "Select assignee..." : "Select project first..."}
                    isDisabled={!workFormData.project_id}
                    onChange={(opt) => {
                      setWorkValidationErrors((prev) => ({ ...prev, assigned_to: "" }));
                      setWorkFormData({
                        ...workFormData,
                        assigned_to: opt ? [opt.value] : []
                      });
                    }}
                    value={(eligibleUsers || [])
                      .filter((u) => workFormData.assigned_to.includes(u._id || u.id))
                      .map((u) => ({ value: u._id || u.id, label: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || u.email }))[0] || null}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: workValidationErrors.assigned_to ? '#f43f5e' : '#e2e8f0',
                        boxShadow: workValidationErrors.assigned_to ? '0 0 0 2px rgba(244, 63, 94, 0.2)' : 'none',
                        borderRadius: "0.5rem"
                      })
                    }}
                  />
                  {workValidationErrors.assigned_to && (
                    <p className="text-[11px] font-semibold text-rose-500 mt-1">{workValidationErrors.assigned_to}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">Description</label>
                  <textarea
                    rows="3"
                    placeholder="Task details..."
                    value={workFormData.task_description}
                    onChange={(e) => setWorkFormData({ ...workFormData, task_description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 focus:outline-none resize-none"
                  ></textarea>
                </div>
              </div>

              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsCreateWorkOpen(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingWork}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {submittingWork ? "Creating..." : "Create Work"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {isEditGroupOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 animate-scaleUp">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">Edit Group</h3>
              <button
                type="button"
                onClick={() => setIsEditGroupOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center text-xs font-bold transition-colors"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditGroupSubmit} className="mt-4 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Group Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Group name..."
                  value={editGroupData.group_name}
                  onChange={(e) => {
                    setEditGroupErrors({});
                    setEditGroupData({ ...editGroupData, group_name: e.target.value });
                  }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none transition-all ${
                    editGroupErrors.group_name
                      ? "border-rose-500 ring-2 ring-rose-500/20"
                      : "border-slate-200 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                  }`}
                />
                {editGroupErrors.group_name && (
                  <p className="text-[11px] font-semibold text-rose-500 mt-1">{editGroupErrors.group_name}</p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditGroupOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingEditGroup}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-xs transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
                >
                  {submittingEditGroup && (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  <span>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Group Modal */}
      {isDeleteGroupOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 animate-scaleUp">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2 text-rose-600">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <h3 className="text-lg font-bold text-slate-900">Delete Group</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsDeleteGroupOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center text-xs font-bold transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="my-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                Are you sure you want to delete the group <span className="font-bold text-slate-900">"{deleteGroupData?.group_name}"</span>?
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Tasks in this group will not be deleted, but they will no longer be assigned to this group.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsDeleteGroupOpen(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteGroupConfirm}
                disabled={submittingDeleteGroup}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
              >
                {submittingDeleteGroup && (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                <span>Delete Group</span>
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Task Row Card Component matching Reference Image 2 with Drag support
const TaskRowCard = ({
  task,
  parentProjectId,
  currentGroupId,
  handleDragStart,
  statusOptions,
  handleInlineStatusChange,
  renderPriorityBadge,
  getStatusColor,
  actionLoadingId
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const taskId = task._id || task.id;
  const taskTitle = task.task_name || task.title || task.name || "Untitled Task";
  const statusName = typeof task.status_name === "object"
    ? (task.status_name?.status_name || task.status_name?.task_status || "ToDo")
    : (task.status_name || task.status || task.task_status?.task_status || "ToDo");
  const priority = task.task_priority || task.priority || "Medium";

  // Assignee initials
  const assignees = task.assigned_to || [];
  const firstAssignee = assignees[0];
  const initials = firstAssignee
    ? `${firstAssignee.first_name?.[0] || ""}${firstAssignee.last_name?.[0] || ""}`.toUpperCase()
    : "AF";

  const dueDateFormatted = task.task_end_date
    ? new Date(task.task_end_date).toLocaleDateString("en-US", { month: "short", day: "2-digit" })
    : "Jul 30";

  return (
    <div
      draggable={true}
      onDragStart={(e) => {
        setIsDragging(true);
        if (handleDragStart) {
          handleDragStart(e, task, parentProjectId, currentGroupId);
        }
      }}
      onDragEnd={() => setIsDragging(false)}
      className={`bg-white rounded-xl border border-slate-200/90 p-2.5 sm:px-3.5 sm:py-2.5 shadow-2xs hover:border-slate-300 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs ${
        isDragging ? "opacity-40 border-dashed border-indigo-400 scale-[0.98]" : ""
      }`}
    >
      {/* Line 1 (<sm): Drag Handle + Full Task Title taking up entire first line */}
      <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 min-w-0">
        <div
          title="Drag to move task to another group or project root"
          className="text-slate-600 hover:text-slate-900 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-slate-100 transition-colors flex items-center shrink-0"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
          </svg>
        </div>

        <Link
          to={`/kpi/works/${taskId}`}
          className="text-sm sm:text-base font-semibold text-slate-900 leading-snug truncate w-full"
        >
          {taskTitle}
        </Link>
      </div>

      {/* Line 2 (<sm): 3 items (Status, Priority, Date) on left end; Avatar & 3-dots Menu on right end */}
      <div className="flex items-center justify-between sm:justify-end gap-2.5 sm:gap-4 w-full sm:w-auto shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-slate-100/80 sm:border-transparent text-xs">
        {/* Left End: 3 items (Status, Priority, Due Date) */}
        <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
          {/* Status Badge */}
          {(() => {
            const style = getStatusBadgeStyle(statusName);
            return (
              <div className={`inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${style.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}></span>
                <span>{formatStatusLabel(statusName)}</span>
              </div>
            );
          })()}

          {/* Priority Badge */}
          {renderPriorityBadge(priority)}

          {/* Due Date */}
          <div className="flex items-center gap-1 text-xs sm:text-sm font-medium text-slate-600 whitespace-nowrap">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span>{dueDateFormatted}</span>
          </div>
        </div>

        {/* Right End: Assignee Avatar + Full Name */}
        {(() => {
          const ownerColor = getOwnerColor(initials);
          const fullName = firstAssignee ? `${firstAssignee.first_name || ''} ${firstAssignee.last_name || ''}`.trim() || firstAssignee.name || firstAssignee.email : "";
          return (
            <div className="flex items-center gap-1.5 bg-[#f7f6f3] hover:bg-[#efeee7] transition-colors px-2 py-0.5 rounded-full border border-slate-200/80 shrink-0">
              <div
                className={`w-5 h-5 rounded-full ${ownerColor} text-white text-[9px] font-bold flex items-center justify-center shrink-0`}
              >
                {initials}
              </div>
              <span className="text-[11px] font-bold text-slate-900 truncate max-w-[110px]">
                {fullName || "Unassigned"}
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default KPIGroups;
